import Dexie, { type EntityTable } from 'dexie'
import type {
  Checklist,
  CampoChecklist,
  Criticidade,
  EstadoFila,
  Obra,
  StatusInspecao,
  Foto,
  Resposta,
} from '@qsms/shared'

/// O banco do dispositivo. É aqui que a inspeção existe de verdade enquanto não há
/// rede — e, mesmo com rede, é aqui que ela nasce.
///
/// Três grupos de tabelas:
///
/// - **cadastros** (`checklists`, `campos`, `obras`, `pessoas`): cópia do servidor,
///   substituída a cada sincronização.
/// - **trabalho** (`inspecoes`, `fotos`): o que o inspetor produziu.
/// - **fila** (`fila`): o que ainda precisa subir.
///
/// Nada é apagado por ter sido enviado: a inspeção continua visível no aparelho até
/// o inspetor sair da obra, e reenviar é sempre seguro.

export interface InspecaoLocal {
  id: string
  codigo: string
  checklistId: string
  obraId: string
  status: StatusInspecao
  titulo: string | null
  dataCriacao: string | null
  assinaturaUsuario: string | null
  /// Revisão do SESMT. Só chega do servidor; o inspetor não define.
  criticidadeApia: Criticidade | null
  registradoEm: string
  atualizadoEm: string
  respostas: Resposta[]
  fotos: Foto[]
  /// Versão conhecida do servidor. `null` enquanto nunca subiu.
  versaoServidor: number | null
  /// Onde está no ciclo de vida do envio.
  sincronizacao: EstadoFila
  /// Mensagem para o usuário quando `sincronizacao` é BLOQUEADO.
  problema: string | null
  /// Só leitura: veio do servidor, não foi feita neste aparelho.
  remota: boolean
  /// Já saiu ao menos uma vez para o servidor, com ou sem resposta. Com
  /// `versaoServidor` ainda nulo, é o que diz que ela pode existir lá: a criação
  /// chegou e a resposta se perdeu, ou o envio está no ar agora.
  tentouEnvio?: boolean
}

/// Foto ainda no aparelho. O binário fica aqui até subir; depois disso o app passa a
/// exibir pela URL da API, mas o blob local só é descartado quando o espaço aperta.
export interface FotoLocal {
  arquivoId: string
  inspecaoId: string
  blob: Blob
  mimeType: string
  criadoEm: string
  enviado: boolean
}

export interface ItemFila {
  id: string
  inspecaoId: string
  /// Muda a cada tentativa de envio de um estado; é o que torna o reenvio seguro.
  clientMutationId: string
  criadoEm: string
  tentativas: number
  proximaTentativa: string
  ultimoErro: string | null
  /// Id de uma mutação que saiu e ficou sem resposta — a rede caiu no meio. O
  /// servidor pode tê-la gravado. O próximo envio repete esse id primeiro, para
  /// saber a versão em que o servidor está; ver `aplicarResultado`.
  mutacaoIncerta?: string | null
}

export interface Pessoa {
  email: string
  nome: string
}

/// Quem pode ser responsável por uma não conformidade, e em que obras. A tela de
/// preenchimento mostra só os da obra da inspeção.
export interface ResponsavelNc {
  id: string
  nome: string
  obraIds: string[]
}

/// Chave/valor para o que não merece tabela: carimbo do último pull, obra escolhida,
/// dados da sessão.
export interface Ajuste {
  chave: string
  valor: unknown
}

class BancoQsms extends Dexie {
  checklists!: EntityTable<Checklist, 'id'>
  campos!: EntityTable<CampoChecklist, 'id'>
  obras!: EntityTable<Obra, 'id'>
  pessoas!: EntityTable<Pessoa, 'email'>
  inspecoes!: EntityTable<InspecaoLocal, 'id'>
  fotos!: EntityTable<FotoLocal, 'arquivoId'>
  fila!: EntityTable<ItemFila, 'id'>
  ajustes!: EntityTable<Ajuste, 'chave'>

  constructor() {
    super('qsms')
    this.version(1).stores({
      checklists: 'id, tipo, nome',
      campos: 'id, checklistId, ordem',
      obras: 'id, codigo',
      pessoas: 'email, nome',
      inspecoes: 'id, obraId, checklistId, status, sincronizacao, registradoEm, remota',
      fotos: 'arquivoId, inspecaoId, enviado',
      fila: 'id, inspecaoId, proximaTentativa',
      ajustes: 'chave',
    })
  }
}

export const banco = new BancoQsms()

// ---------------------------------------------------------------- ajustes

export async function lerAjuste<T>(chave: string, padrao: T): Promise<T> {
  const linha = await banco.ajustes.get(chave)
  return linha ? (linha.valor as T) : padrao
}

export async function gravarAjuste(chave: string, valor: unknown): Promise<void> {
  await banco.ajustes.put({ chave, valor })
}

export const CHAVES = {
  ultimoPullCadastros: 'ultimoPullCadastros',
  ultimoPullInspecoes: 'ultimoPullInspecoes',
  exclusoesReconciliadas: 'exclusoesReconciliadas',
  obraSelecionada: 'obraSelecionada',
  checklistSelecionado: 'checklistSelecionado',
  sessao: 'sessao',
  /// `{ nome do campo: nomes }` — os campos que se escolhem numa lista, como o
  /// Gerente Contrato. Vem inteiro a cada sincronização de cadastros.
  listasDePessoas: 'listasDePessoas',
  /// `ResponsavelNc[]` — a lista de responsáveis por não conformidade. Vem
  /// inteira a cada sincronização de cadastros.
  responsaveisNc: 'responsaveisNc',
} as const

// ---------------------------------------------------------------- limpeza

/// Apaga o que é do usuário anterior. Chamado no logout e quando o servidor não
/// reconhece mais a sessão — um aparelho compartilhado entre turnos não pode deixar
/// a inspeção de um aparecer para o outro.
export async function limparDadosDoUsuario(): Promise<void> {
  await banco.transaction(
    'rw',
    [banco.inspecoes, banco.fotos, banco.fila, banco.ajustes, banco.obras, banco.pessoas],
    async () => {
      await Promise.all([
        banco.inspecoes.clear(),
        banco.fotos.clear(),
        banco.fila.clear(),
        banco.obras.clear(),
        banco.pessoas.clear(),
      ])
      // Checklists e campos ficam: são iguais para todos e caros de rebaixar.
      await banco.ajustes.bulkDelete([
        CHAVES.sessao,
        CHAVES.obraSelecionada,
        CHAVES.checklistSelecionado,
        CHAVES.ultimoPullInspecoes,
      ])
    },
  )
}

/// Libera espaço descartando blobs de fotos já enviadas, das mais antigas para as
/// mais novas. A foto continua visível pela API quando houver rede.
export async function liberarEspaco(manterUltimos = 200): Promise<number> {
  const enviadas = await banco.fotos.where('enviado').equals(1 as never).toArray()
  if (enviadas.length <= manterUltimos) return 0

  const descartar = enviadas
    .sort((a, b) => a.criadoEm.localeCompare(b.criadoEm))
    .slice(0, enviadas.length - manterUltimos)

  await banco.fotos.bulkDelete(descartar.map((f) => f.arquivoId))
  return descartar.length
}

/// Quanto o navegador nos deu e quanto usamos. A tela de ajustes mostra isso — numa
/// obra longa, o inspetor precisa saber que está perto do limite antes de perder foto.
export async function espacoUsado(): Promise<{ usado: number; disponivel: number } | null> {
  if (!navigator.storage?.estimate) return null
  const e = await navigator.storage.estimate()
  return { usado: e.usage ?? 0, disponivel: e.quota ?? 0 }
}

/// Tira uma inspeção deste aparelho, com tudo o que é dela.
///
/// A fila vai junto: sem isso, uma inspeção apagada continuaria sendo reenviada.
/// E as fotos também, que são o grosso do espaço ocupado.
export async function removerInspecaoLocal(id: string): Promise<void> {
  await banco.transaction('rw', banco.inspecoes, banco.fila, banco.fotos, async () => {
    await banco.fila.where('inspecaoId').equals(id).delete()
    await banco.fotos.where('inspecaoId').equals(id).delete()
    await banco.inspecoes.delete(id)
  })
}
